import { LogService } from 'src/app/core/helpers/log.service';
import { catchError, take, tap, filter, switchMap } from 'rxjs/operators';
import { Team } from '@models';
import { Injectable } from '@angular/core';
import { BehaviorSubject, EMPTY, of } from 'rxjs';

import { TeamService } from './api/team.service';
import { ToastService } from './api/toast.service';

@Injectable({
  providedIn: 'root',
})
export class TeamSelectionService {
  private readonly selectedTeamIdKey = 'selectedTeamId';
  teamBehaviorSubject$ = new BehaviorSubject<Team>(undefined);
  private memberOfTeamsBehaviorSubject$ = new BehaviorSubject<Team[]>(undefined);

  get team() {
    return this.teamBehaviorSubject$.getValue();
  }

  team$ = this.teamBehaviorSubject$.asObservable();
  memberOfTeams$ = this.memberOfTeamsBehaviorSubject$.asObservable();

  constructor(
    private teamService: TeamService,
    private logService: LogService,
    private toastService: ToastService
  ) {
    // Laad geautoriseerde teams en valideer het opgeslagen team
    this.teamService.authorizedTeams$
      .pipe(
        tap((teams) => this.logService.info('teamselectionservice', teams)),
        switchMap((teams) => {
          if (teams === undefined) {
            this.memberOfTeamsBehaviorSubject$.next(undefined);
            this.teamBehaviorSubject$.next(undefined);
            return of(null);
          }
          if (this.teamService.authorizedTeamsFromCache) {
            this.memberOfTeamsBehaviorSubject$.next(undefined);
            this.teamBehaviorSubject$.next(undefined);
            return of(null);
          }
          const selectedTeamId = localStorage.getItem(this.selectedTeamIdKey);
          this.memberOfTeamsBehaviorSubject$.next(teams);

          // Als we een opgeslagen team-ID hebben, valideer deze
          if (selectedTeamId) {
            // Controleer of de opgeslagen team-ID in de lijst met geautoriseerde teams staat
            const isValidTeam = teams.some((team) => team.globalId === selectedTeamId);

            if (isValidTeam) {
              // Als geldig, laad de volledige teamgegevens
              return this.teamService.getById(selectedTeamId).pipe(
                take(1),
                catchError(() => {
                  this.teamBehaviorSubject$.next(undefined);
                  this.toastService.showError('Het laden van het geselecteerde team is mislukt. Probeer het opnieuw.');
                  return EMPTY;
                })
              );
            } else {
              // Als niet geldig, log het probleem en verwijder uit localStorage
              this.logService.warning(
                'TeamSelectionService',
                `Opgeslagen team-ID ${selectedTeamId} komt niet voor in de geautoriseerde teams van de gebruiker. Wordt verwijderd.`
              );
              localStorage.removeItem(this.selectedTeamIdKey);

              // Stel standaardteam in als er maar één team beschikbaar is
              if (teams.length === 1 && !this.teamService.authorizedTeamsFromCache) {
                this.setTeam(teams[0]);
              } else {
                this.teamBehaviorSubject$.next(null);
              }
              return of(null);
            }
          } else {
            // Als er geen opgeslagen team is maar er is precies één team beschikbaar, selecteer deze
            if (teams.length === 1) {
              this.setTeam(teams[0]);
            } else {
              this.teamBehaviorSubject$.next(null);
            }
            return of(null);
          }
        })
      )
      .subscribe((team) => {
        if (team && localStorage.getItem(this.selectedTeamIdKey) === team.globalId) {
          this.logService.info('TeamSelectionService', 'Gevalideerd team uit opslag wordt ingesteld', team);
          this.teamBehaviorSubject$.next(team);
        }
      });
  }

  setTeam(team: Team) {
    console.log('setTeam', team);
    if (team) {
      const teams = this.memberOfTeamsBehaviorSubject$.value;
      if (teams && !teams.some((authorized) => authorized.globalId === team.globalId)) return;
      localStorage.setItem(this.selectedTeamIdKey, team.globalId);
      this.teamBehaviorSubject$.next(teams && !this.teamService.authorizedTeamsFromCache ? team : undefined);
    } else {
      localStorage.removeItem(this.selectedTeamIdKey);
      this.teamBehaviorSubject$.next(team);
    }
  }

  // Nieuwe methode om team te valideren wanneer nodig
  validateStoredTeam() {
    const selectedTeamId = localStorage.getItem(this.selectedTeamIdKey);
    if (!selectedTeamId) return;

    this.teamService.authorizedTeams$
      .pipe(
        filter((teams) => teams !== undefined && !this.teamService.authorizedTeamsFromCache),
        take(1)
      )
      .subscribe((teams) => {
        if (localStorage.getItem(this.selectedTeamIdKey) !== selectedTeamId) return;
        const isValidTeam = teams.some((team) => team.globalId === selectedTeamId);
        if (!isValidTeam) {
          this.logService.warning('TeamSelectionService', `Opgeslagen team-ID ${selectedTeamId} is niet meer geldig. Wordt gereset.`);
          localStorage.removeItem(this.selectedTeamIdKey);

          if (teams.length === 1) {
            this.setTeam(teams[0]);
          } else {
            this.teamBehaviorSubject$.next(null);
          }
        }
      });
  }
}
